import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import styles from "../styles/pages/Templates.module.css";
import TemplatesToolbar from "../components/templates/TemplatesToolbar";
import TemplatesTable from "../components/templates/TemplatesTable";
import TemplateFileCard from "../ui/cards/TemplateFileCard";
import { templatesService } from "../services/templates.service";
import backIcon from "../assets/icons/backIcon.svg";
import IconButton from "../ui/buttons/IconButton";
import TemplateFilePreview from "../components/templates/TemplateFilePreview";
import FloatingModal from "../ui/Modals/FloatingModal";
import Button from "../ui/buttons/Button";
import { getCurrentTheme } from "../utils/themeUtils";
import NewAttachmentModal from "../components/templates/NewAttachmentModal";
import { ConfirmDeleteModal } from "../components/admin/AdminDetailModals";
import TemplaeteGroupModal from "../components/templates/TemplateGroupModal";
import LoadingScreen from "../ui/loadingScreen/LoadingScreen";

const BackIcon = (
  <img
    src={backIcon}
    alt=""
    className={styles.backIcon}
    style={{ width: 32, height: 32, display: "block" }}
  />
);

const TemplatesPage = ({ onOpenWhatsApp } = {}) => {
  const location = useLocation();
  const pendingTemplateGroupId = useRef(
    location.state?.openTemplateGroupId ?? null,
  );
  const [viewMode, setViewMode] = useState(
    location.state?.viewMode === "attachments" ? "attachments" : "templates",
  );
  const viewModeRef = useRef(viewMode);

  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);
  const [templates, setTemplates] = useState([]);

  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [selectedTemplateFiles, setSelectedTemplateFiles] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [theme, setTheme] = useState(getCurrentTheme);

  const [showTemplateInfo, setShowTemplateInfo] = useState(false);
  const [lateralModalData, setLateralModalData] = useState({});

  const filterFromViewMode = viewMode === "attachments" ? "op2" : "op1";

  const [newTemplateModalState, setNewTemplateModalState] = useState(false);
  const [draggedTemplateFile, setDraggedTemplateFile] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isInactiveDropTarget, setIsInactiveDropTarget] = useState(false);
  const [updatingTemplateFileId, setUpdatingTemplateFileId] = useState(null);

  const [templateMessageError, setTemplateMessageError] = useState("");
  const [attachmentInUseError, setAttachmentInUseError] = useState(null);

  const [attachments, setAttachments] = useState([]);
  const [newAttachmentModalState, setNewAttachmentModalState] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [newTemplateGroupModalState, setNewTemplateGroupModalState] =
    useState(false);
  const [editingTemplateGroup, setEditingTemplateGroup] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(getCurrentTheme());
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  const selectTemplate = useCallback((template, openTemplateFileId = null) => {
    setSelectedTemplate(template);
    setSelectedTemplateFiles([]);
    templatesService.fetchTemplateFiles(template.id).then((response) => {
      const files = response.results || [];
      setSelectedTemplateFiles(files);

      if (!openTemplateFileId) return;

      const fileToOpen = files.find((file) => file.id === openTemplateFileId);

      if (fileToOpen) {
        setLateralModalData(fileToOpen);
        setShowTemplateInfo(true);
      }
    });
  }, []);

  const fetchTemplates = useCallback(
    ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      templatesService
        .fetchTemplateGroups()
        .then((response) => {
          const data = Array.isArray(response)
            ? response
            : (response?.results ?? []);
          setTemplates(data);
          if (viewModeRef.current === "templates") setFilteredTemplates(data);
          if (pendingTemplateGroupId.current) {
            const target = data.find(
              (templateGroup) =>
                templateGroup.id === pendingTemplateGroupId.current,
            );
            pendingTemplateGroupId.current = null;
            if (target) selectTemplate(target);
          }
        })
        .catch((err) => console.error(err))
        .finally(() => {
          if (!silent) setLoading(false);
        });
    },
    [selectTemplate],
  );

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const fetchAttachmentsFiles = useCallback(async () => {
    try {
      const response = await templatesService.fetchTemplateFilesAttachments();

      const data = response.results || [];

      setAttachments(data);

      if (viewMode === "attachments") {
        setFilteredTemplates(data);
      }
    } catch (err) {
      console.error(err);
    }
  }, [viewMode]);

  useEffect(() => {
    fetchAttachmentsFiles();
  }, [fetchAttachmentsFiles]);

  const handleFilterChange = (value) => {
    if (value === "op3") {
      onOpenWhatsApp?.();
      return;
    }
    if (value === "op2") {
      setViewMode("attachments");
      setFilteredTemplates(attachments);
    } else {
      setViewMode("templates");
      setFilteredTemplates(templates);
    }
  };

  function handleSearch(term) {
    const source = viewMode === "attachments" ? attachments : templates;

    if (!term.trim()) {
      setFilteredTemplates(source);
      return;
    }

    const lower = term.toLowerCase();

    const filtered = source.filter((item) => {
      if (viewMode === "attachments") {
        return item.name?.toLowerCase().includes(lower);
      }

      return (item.group?.name ?? item.name ?? "")
        .toLowerCase()
        .includes(lower);
    });

    setFilteredTemplates(filtered);
  }

  const isTemplateSelected = Boolean(selectedTemplate?.id);

  const handleLateralModal = (templateData) => {
    setLateralModalData(templateData);
    setShowTemplateInfo(true);
  };

  // Un file sin is_active (respuestas viejas del backend) se trata como activo.
  const activeTemplateFiles = selectedTemplateFiles
    .filter((file) => file.is_active !== false)
    .sort((a, b) => a.orden - b.orden);

  const inactiveTemplateFiles = selectedTemplateFiles
    .filter((file) => file.is_active === false)
    .sort((a, b) => a.orden - b.orden);

  const nextTemplateFileNumber = activeTemplateFiles.length + 1;

  // El backend recalcula orden, secuencia y max_number del grupo en cada
  // PATCH, así que se vuelve a pedir la lista en lugar de reordenar localmente.
  const refreshTemplateFiles = useCallback(async () => {
    if (!selectedTemplate?.id) return;

    const response = await templatesService.fetchTemplateFiles(
      selectedTemplate.id,
    );
    setSelectedTemplateFiles(response.results || []);
  }, [selectedTemplate]);

  const clearDragState = () => {
    setDraggedTemplateFile(null);
    setDragOverIndex(null);
    setIsInactiveDropTarget(false);
  };

  const handleDropOnActiveZone = async (dropIndex) => {
    if (!draggedTemplateFile || updatingTemplateFileId) return;

    const draggedFile = draggedTemplateFile;

    clearDragState();
    setUpdatingTemplateFileId(draggedFile.id);

    try {
      if (draggedFile.is_active === false) {
        // Reactivar en la posición donde se soltó, contada sobre los activos.
        const newOrder = Math.min(dropIndex, activeTemplateFiles.length) + 1;

        await templatesService.updateTemplateFiles(draggedFile.id, {
          is_active: true,
          orden: newOrder,
        });

        await refreshTemplateFiles();
        fetchTemplates({ silent: true });
      } else {
        const filesWithoutDragged = activeTemplateFiles.filter(
          (file) => file.id !== draggedFile.id,
        );

        const reorderedFiles = [...filesWithoutDragged];

        reorderedFiles.splice(dropIndex, 0, draggedFile);

        const newOrder =
          reorderedFiles.findIndex((file) => file.id === draggedFile.id) + 1;

        await templatesService.updateTemplateFiles(draggedFile.id, {
          orden: newOrder,
        });

        await refreshTemplateFiles();
      }
    } catch (error) {
      console.error("Error updating template file order:", error);
      setTemplateMessageError(
        error?.response?.data?.detail ||
          "No se pudo actualizar el template. Intentá de nuevo.",
      );
    } finally {
      setUpdatingTemplateFileId(null);
    }
  };

  const handleDropOnInactiveZone = async () => {
    if (!draggedTemplateFile || updatingTemplateFileId) return;

    const draggedFile = draggedTemplateFile;

    clearDragState();

    if (draggedFile.is_active === false) return;

    setUpdatingTemplateFileId(draggedFile.id);

    try {
      await templatesService.updateTemplateFiles(draggedFile.id, {
        is_active: false,
      });

      await refreshTemplateFiles();
      fetchTemplates({ silent: true });
    } catch (error) {
      console.error("Error deactivating template file:", error);
      setTemplateMessageError(
        error?.response?.data?.detail ||
          "No se pudo desactivar el template. Intentá de nuevo.",
      );
    } finally {
      setUpdatingTemplateFileId(null);
    }
  };

  const handleDownload = async (fileUrl) => {
    if (!fileUrl || typeof fileUrl !== "string") {
      setTemplateMessageError(
        "No se encontró una URL válida para descargar el archivo.",
      );
      return;
    }

    try {
      const response = await fetch(fileUrl);

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      const filename = fileUrl.split("/").pop()?.split("?")[0] || "attachment";

      link.download = filename;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const handlePreviewFile = (fileUrl) => {
    if (!fileUrl) return;

    window.open(fileUrl, "_blank", "noopener,noreferrer");
  };

  const deleteAttachment = async () => {
    if (!attachmentToDelete?.id) {
      setAttachmentToDelete(null);
      return;
    }

    try {
      await templatesService.deleteTemplateFileAttachment(
        attachmentToDelete.id,
      );
      await fetchAttachmentsFiles();
    } catch (error) {
      console.error("Error deleting attachment:", error);

      const errorData = error?.response?.data;

      if (errorData?.template_files?.length) {
        setTemplateMessageError("");
        setAttachmentInUseError({
          detail:
            errorData.detail || "No se pudo eliminar el archivo adjunto.",
          templateFiles: errorData.template_files,
        });
      } else {
        setAttachmentInUseError(null);
        setTemplateMessageError(
          errorData?.detail || "No se pudo eliminar el archivo adjunto.",
        );
      }
    } finally {
      setAttachmentToDelete(null);
    }
  };

  const openLinkedTemplateFile = (linkedTemplateFile) => {
    setAttachmentInUseError(null);

    const targetGroup = templates.find(
      (templateGroup) =>
        templateGroup.id === linkedTemplateFile.template_group_id,
    );

    if (!targetGroup) {
      setTemplateMessageError(
        "Ese template file pertenece a un grupo al que no tenés acceso, " +
          "por lo que no se puede abrir desde acá.",
      );
      return;
    }

    setViewMode("templates");
    setFilteredTemplates(templates);
    selectTemplate(targetGroup, linkedTemplateFile.id);
  };

  const handleNewTemplateGroup = () => {
    setNewTemplateGroupModalState(true);
  };

  const handleEditTemplateGroup = (row) => {
    setEditingTemplateGroup(row);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className={styles.templates}>
      <div className={styles.templatesContent}>
        <div className={styles.templatesBody}>
          <div className={styles.frameParent}>
            <div className={styles.templatesWrapper}>
              {isTemplateSelected && (
                <IconButton
                  tone="neutral"
                  size="md"
                  icon={BackIcon}
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedTemplateFiles([]);
                    setSelectedTemplate(null);
                  }}
                  aria-label="volver"
                />
              )}
              <div className={styles.div}>
                {isTemplateSelected
                  ? `${selectedTemplate?.group?.name ?? selectedTemplate?.name ?? ""}`
                  : "Templates"}
              </div>
            </div>
            <div className={styles.frameParent}>
              <TemplatesToolbar
                onSearchChange={handleSearch}
                onFilterChange={handleFilterChange}
                onOpenReview={() => setViewMode("attachments")}
                selectedFilter={filterFromViewMode}
                onNewTemplateFile={() => {
                  if (!selectedTemplate?.id) return;

                  setNewTemplateModalState(true);
                }}
                isTemplateSelected={isTemplateSelected}
                onUploadAttachmentFile={() => setNewAttachmentModalState(true)}
                onNewTemplateGroup={handleNewTemplateGroup}
              />
              {isTemplateSelected ? (
                <div
                  style={{
                    gap: "1.5rem",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      marginLeft: 10,
                    }}
                  >
                    Templates / {selectedTemplate?.group?.name ?? selectedTemplate?.name}
                  </div>
                  <div className={styles.cardsContainer}>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "flex-start",
                        gap: "36px",
                        width: "100%",
                        overflowX: "hidden",
                      }}
                      onDragOver={(event) => {
                        event.preventDefault();

                        if (!draggedTemplateFile) return;

                        // Sobre el espacio libre del contenedor: soltar al final.
                        setDragOverIndex(activeTemplateFiles.length);
                        setIsInactiveDropTarget(false);
                      }}
                      onDrop={(event) => {
                        event.preventDefault();

                        if (dragOverIndex === null) return;

                        handleDropOnActiveZone(dragOverIndex);
                      }}
                    >
                      {activeTemplateFiles.length === 0 ? (
                        <p className={styles.emptyTemplateFilesText}>
                          {selectedTemplateFiles.length === 0
                            ? "No hay Template Files cargados"
                            : "No hay Template Files activos"}
                        </p>
                      ) : (
                        activeTemplateFiles.map((file, index) => {
                          const dropPosition =
                            dragOverIndex === index
                              ? "before"
                              : dragOverIndex === index + 1
                                ? "after"
                                : null;

                          return (
                            <TemplateFileCard
                              key={file.id}
                              file={file}
                              theme={theme}
                              draggable
                              dropPosition={dropPosition}
                              isUpdating={updatingTemplateFileId === file.id}
                              onClick={handleLateralModal}
                              onDragStart={() => setDraggedTemplateFile(file)}
                              onDragOver={(event) => {
                                event.preventDefault();
                                event.stopPropagation();

                                const rect =
                                  event.currentTarget.getBoundingClientRect();
                                const isAfter =
                                  event.clientX > rect.left + rect.width / 2;

                                setDragOverIndex(index + (isAfter ? 1 : 0));
                                setIsInactiveDropTarget(false);
                              }}
                              onDrop={(event) => {
                                event.preventDefault();
                                event.stopPropagation();

                                if (dragOverIndex === null) return;

                                handleDropOnActiveZone(dragOverIndex);
                              }}
                              onDragEnd={clearDragState}
                            />
                          );
                        })
                      )}
                    </div>
                  </div>
                  {selectedTemplateFiles.length > 0 && (
                    <div className={styles.inactiveSection}>
                      <div className={styles.inactiveSectionTitle}>
                        Templates inactivos
                      </div>
                      <div
                        data-testid="inactive-drop-zone"
                        className={`${styles.inactiveDropZone} ${
                          isInactiveDropTarget
                            ? styles.inactiveDropZoneActive
                            : ""
                        }`}
                        onDragOver={(event) => {
                          event.preventDefault();

                          setDragOverIndex(null);
                          setIsInactiveDropTarget(
                            Boolean(draggedTemplateFile) &&
                              draggedTemplateFile.is_active !== false,
                          );
                        }}
                        onDragLeave={(event) => {
                          if (
                            event.relatedTarget instanceof Node &&
                            event.currentTarget.contains(event.relatedTarget)
                          ) {
                            return;
                          }

                          setIsInactiveDropTarget(false);
                        }}
                        onDrop={(event) => {
                          event.preventDefault();

                          handleDropOnInactiveZone();
                        }}
                      >
                        {inactiveTemplateFiles.length === 0 ? (
                          <p className={styles.inactiveEmptyText}>
                            Arrastrá un template aquí para desactivarlo
                          </p>
                        ) : (
                          inactiveTemplateFiles.map((file) => (
                            <TemplateFileCard
                              key={file.id}
                              file={file}
                              theme={theme}
                              draggable
                              isUpdating={updatingTemplateFileId === file.id}
                              onClick={handleLateralModal}
                              onDragStart={() => setDraggedTemplateFile(file)}
                              onDragEnd={clearDragState}
                            />
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <TemplatesTable
                  rows={filteredTemplates}
                  onSelectTemplate={selectTemplate}
                  onEditTemplate={
                    viewMode === "templates"
                      ? handleEditTemplateGroup
                      : undefined
                  }
                  mode={viewMode}
                  handleDownload={handleDownload}
                  onShowFile={handlePreviewFile}
                  onDeleteAttachment={setAttachmentToDelete}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {showTemplateInfo && (
        <TemplateFilePreview
          data={lateralModalData}
          onClose={() => setShowTemplateInfo(false)}
          account={selectedTemplate?.account}
          reCallTemplates={() => selectTemplate(selectedTemplate)}
          onError={setTemplateMessageError}
        />
      )}
      {newTemplateModalState && (
        <TemplateFilePreview
          data={null}
          onClose={() => setNewTemplateModalState(false)}
          account={selectedTemplate?.account}
          reCallTemplates={() => selectTemplate(selectedTemplate)}
          templateGroupId={selectedTemplate?.id}
          defaultOrden={nextTemplateFileNumber}
          onError={setTemplateMessageError}
        />
      )}
      {templateMessageError && (
        <FloatingModal direction="column" zIndex={10000}>
          <span style={{ whiteSpace: "pre-line" }}>{templateMessageError}</span>
          <Button onClick={() => setTemplateMessageError("")}>OK</Button>
        </FloatingModal>
      )}
      {attachmentInUseError && (
        <FloatingModal direction="column" zIndex={10000}>
          <span style={{ whiteSpace: "pre-line" }}>
            {attachmentInUseError.detail}
          </span>
          <div className={styles.linkedTemplateFilesList}>
            {attachmentInUseError.templateFiles.map((linkedTemplateFile) => (
              <button
                key={linkedTemplateFile.id}
                type="button"
                className={styles.linkedTemplateFileLink}
                onClick={() => openLinkedTemplateFile(linkedTemplateFile)}
              >
                {linkedTemplateFile.template_group_name} /{" "}
                {linkedTemplateFile.name}
              </button>
            ))}
          </div>
          <Button onClick={() => setAttachmentInUseError(null)}>OK</Button>
        </FloatingModal>
      )}
      {newAttachmentModalState && (
        <NewAttachmentModal
          onClose={() => setNewAttachmentModalState(false)}
          onNewAttachmentError={setTemplateMessageError}
          onAttachmentCreated={fetchAttachmentsFiles}
        />
      )}
      {attachmentToDelete && (
        <ConfirmDeleteModal
          title="¿Eliminar este archivo adjunto?"
          description="Esta acción no tiene vuelta atrás. Si el archivo está en uso por algún template, primero desvinculalo."
          name={attachmentToDelete.name}
          onConfirm={deleteAttachment}
          onClose={() => setAttachmentToDelete(null)}
        />
      )}
      {newTemplateGroupModalState && (
        <TemplaeteGroupModal
          onClose={() => setNewTemplateGroupModalState(false)}
          onNewTemplateGroupError={setTemplateMessageError}
          onTemplateGroupCreated={() => {
            fetchTemplates();
          }}
        />
      )}
      {editingTemplateGroup && (
        <TemplaeteGroupModal
          initialData={editingTemplateGroup}
          onClose={() => setEditingTemplateGroup(null)}
          onNewTemplateGroupError={setTemplateMessageError}
          onTemplateGroupCreated={() => {
            setEditingTemplateGroup(null);
            fetchTemplates();
          }}
        />
      )}
    </div>
  );
};

export default TemplatesPage;
